/**
 * Thin client helper for the `decideFamilyChangeRequest` admin mutation
 * (#127 slice 5, `.agents/tasks/127-family-requests-contract.md`, binding).
 *
 * decideFamilyChangeRequest(requestId: String!, decision: String!, reason:
 * String, adminOverrideReason: String): AWSJSON
 *
 * The response is `JSON.stringify(...)`'d by the Lambda, so it's parsed
 * with `parseLambdaResponse` the same way `manageFamilyClient.ts` (#127
 * slice 4) parses `manageFamily`'s response.
 */
import { generateClient } from 'aws-amplify/data';
import { parseLambdaResponse } from '../../../utils/parseLambdaResponse';

const client = generateClient({ authMode: 'userPool' });

export type FamilyChangeDecision = 'APPROVE' | 'REJECT';

export interface DecideFamilyChangeRequestSuccess {
  success: true;
  refundCents?: number;
  message?: string;
}

export interface DecideFamilyChangeRequestFailure {
  success: false;
  error: string;
  message: string;
}

export type DecideFamilyChangeRequestResult =
  | DecideFamilyChangeRequestSuccess
  | DecideFamilyChangeRequestFailure;

/**
 * Calls `decideFamilyChangeRequest`. Never throws for a server-reported
 * failure (callers check `result.success`); throws only when no usable
 * response could be parsed at all (a transport failure).
 */
export async function callDecideFamilyChangeRequest(args: {
  requestId: string;
  decision: FamilyChangeDecision;
  reason?: string;
  adminOverrideReason?: string;
}): Promise<DecideFamilyChangeRequestResult> {
  const raw = await (client.mutations as any).decideFamilyChangeRequest({
    requestId: args.requestId,
    decision: args.decision,
    reason: args.reason,
    adminOverrideReason: args.adminOverrideReason,
  });
  const parsed = parseLambdaResponse<DecideFamilyChangeRequestResult>(raw?.data);
  if (!parsed) {
    throw new Error('No response received from the server. Please try again.');
  }
  return parsed;
}
